import {FC} from 'react'
import {Redirect, useHistory, useLocation} from 'react-router-dom'
import queryString from 'query-string'
import {useDispatch} from 'react-redux'
import {SubmitHandler} from 'react-hook-form'
import {generateDynamicPath, WITH_AUTH_ADMIN_PATHS} from 'consts/paths'
import {LoadingPage} from 'components/ui/LoadingPage'
import {useQuery} from 'hooks/graphql'
import {asyncError} from 'store/actions/asyncError'
import {notifyError, notifySuccess} from 'store/actions/notify'
import {useRequestProcedureInput} from 'features/procedures/hooks/useRequestProcedureInput'
import {useStartProcedureStatus} from 'features/procedures/hooks/useStartProcedureStatus'
import {getRequestProcedureErrors} from 'features/procedures/request'
import {
  EXTEND_CHILDCARE_REQUEST,
  ExtendChildcareRequestQueryResult,
  ExtendChildcareRequestQueryVariables,
} from './query'
import {Presenter} from './Presenter'
import {Schema} from './schema'

interface RequestWithParamsProps {
  employeeId: string
}

const RequestWithParams: FC<RequestWithParamsProps> = ({employeeId}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const {data, loading} = useQuery<ExtendChildcareRequestQueryResult, ExtendChildcareRequestQueryVariables>(
    EXTEND_CHILDCARE_REQUEST,
    {
      variables: {employeeId},
      fetchPolicy: 'network-only',
    }
  )
  const [requestProcedureInput] = useRequestProcedureInput()
  const [startProcedureStatus] = useStartProcedureStatus()

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    try {
      await requestProcedureInput({
        variables: {
          input: {
            employeeId,
            procedureType: 'extend_childcare',
            message: data.message || undefined,
          },
        },
      })
      dispatch(notifySuccess('依頼しました'))
      history.push('/dashboard')
    } catch (err) {
      const messages = getRequestProcedureErrors(err)
      if (messages) {
        for (const message of messages) {
          dispatch(notifyError(message))
        }
        return
      }

      dispatch(asyncError(err))
    }
  }

  const onSkip: SubmitHandler<Schema> = async () => {
    try {
      const res = await startProcedureStatus({
        variables: {
          input: {
            employeeId,
            procedureType: 'extend_childcare',
            status: 'draft',
            initialized: false,
          },
        },
      })
      const id = res.data?.startProcedureStatus.procedureStatus.id
      if (id) {
        history.push(
          generateDynamicPath(WITH_AUTH_ADMIN_PATHS.EXTEND_CHILDCARE.FLOW, [{pattern: 'id', replacement: id}])
        )
      }
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  if (loading || !data) {
    return (
      <div className="u-pt10">
        <LoadingPage />
      </div>
    )
  }

  return <Presenter employee={data.client.employee} onSubmit={onSubmit} onSkip={onSkip} />
}

interface SearchParams {
  employee_id?: string
}

export const Request: FC = () => {
  const {search} = useLocation()
  const {employee_id: employeeId}: SearchParams = queryString.parse(search)

  if (!employeeId) {
    return <Redirect to={WITH_AUTH_ADMIN_PATHS.EXTEND_CHILDCARE.INDEX} />
  }

  return <RequestWithParams employeeId={employeeId} />
}
